// Common
import { Component, inject, Input, OnInit } from '@angular/core';
import { heightAnimation } from '@modules/common/animations/height.animation';
import { type CalendarRailScope } from '../sidebar-context-menu/calendar-rail-menu';

// Types
import { Calendar } from '@modules/calendar-app/types/calendar';
import { CalendarEvent } from '@modules/calendar-app/types/calendar-event';
import { CalendarsFilters } from '@modules/calendar-app/types/calendars-filters';
import { VirtualFolder } from '@modules/calendar-app/types/virtual-folder';
import { SidebarSplitViewKey } from '@modules/common/types/sidebar-split-view-key';
import { StateKey } from '@modules/settings/types/state-key';

import { type HttpContext } from '@angular/common/http';
import { mailboxTenantContext } from '@modules/common/interceptors/spaces.interceptor';
import { CalendarsService } from '../../services/calendars.service';
import { Application } from '@modules/common/types/application';

// Services
import { DisplayTimeZoneService } from '@modules/common/services/display-timezone.service';

// RX
import { combineLatest } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

// Components
import { CalendarsListStateService } from '@modules/calendar-app/services/calendars-list-state.service';
import { BaseSidebarContainersItemComponent } from '@modules/common/components/base-sidebar-containers-item/base-sidebar-containers-item.component';

@Component({
  selector: 'app-side-bar-calendars',
  templateUrl: './side-bar-calendars.component.html',
  styleUrls: [
    '../../../common/components/base-sidebar-containers-item/base-sidebar-containers-item.component.less',
    './side-bar-calendars.component.less',
  ],
  animations: [heightAnimation],
  standalone: false,
  providers: [
    ...CalendarsListStateService.providers({
      stateKey: StateKey.sidebarCalendarsListState,
    }),
  ],
})
export class SideBarCalendarsComponent
  extends BaseSidebarContainersItemComponent<Calendar, CalendarEvent, CalendarsFilters, VirtualFolder>
  implements OnInit
{
  @Input() public tenantId: string | null | undefined;
  @Input() public title = 'Calendars';
  @Input() public depth = 0;
  /** A workspace section of the rail: the header gets the prototype menu, an options button and a semantic plus. */
  @Input() public rail = false;
  /** Which workspace the section is: the member's My Workspace or the Team Workspace. */
  @Input() public railScope: CalendarRailScope = 'mine';

  protected get railWorkspace(): string {
    return this.railScope === 'team' ? 'Team' : 'My';
  }
  private readonly calendarsService = inject(CalendarsService);

  protected createCalendar(calendar: Calendar): void {
    this.calendarsService
      .create(calendar, { emit: true, toast: true, requestContext: this.railRequestContext() })
      .pipe(takeUntil(this.alive))
      .subscribe(() => this.quickFormPopoverClose.next());
  }

  protected override railRequestContext(): HttpContext | undefined {
    return this.tenantId === undefined ? undefined : mailboxTenantContext(this.tenantId);
  }

  application = Application.calendar;
  sidebarSplitViewKey = SidebarSplitViewKey.calendar;

  private readonly calendarsListStateService = inject(CalendarsListStateService);
  private readonly displayTimeZoneService = inject(DisplayTimeZoneService);

  public override ngOnInit() {
    super.ngOnInit();
    combineLatest([this.calendarsListStateService.value(), this.displayTimeZoneService.resolvedTimeZone$])
      .pipe(takeUntil(this.alive))
      .subscribe(([state, displayTimeZone]) => {
        // `displayTimeZone` FIRST: applyListState reads it to reckon its day windows in the account
        // zone (SBUX-829).
        this.filters = new CalendarsFilters({ displayTimeZone }).applyListState(state);
      });
  }
}
